import { Menu, Search } from "lucide-react";
import { useEffect, useRef, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useAuth } from "@/app/auth/context";
import { IconButton } from "@/components/ui/IconButton";
import { isTypingTarget } from "@/lib/keyboard";
import { PHONE_QUERY, useMediaQuery } from "@/lib/use-media-query";

import { ThemeMenu } from "./ThemeMenu";
import { UserMenu } from "./UserMenu";

export function Header({ menuOpen, onOpenMenu }: { menuOpen: boolean; onOpenMenu: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const phone = useMediaQuery(PHONE_QUERY);
  const { can } = useAuth();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "/" && !event.ctrlKey && !event.metaKey && !isTypingTarget(event.target)) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = inputRef.current?.value.trim() ?? "";
    // Without full-text search the box still finds tickets by number or title.
    const target = can("search.fulltext") ? "/search" : "/tickets";
    void navigate(query ? `${target}?q=${encodeURIComponent(query)}` : target);
  };

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-line bg-surface/90 px-4 backdrop-blur sm:px-6">
      <IconButton
        label={t("nav.openMenu")}
        onClick={onOpenMenu}
        className="md:hidden"
        aria-controls="app-sidebar"
        aria-expanded={menuOpen}
      >
        <Menu className="size-5" aria-hidden="true" />
      </IconButton>

      <form role="search" onSubmit={onSubmit} className="relative w-full max-w-xl">
        <label htmlFor="global-search" className="sr-only">
          {t("header.searchLabel")}
        </label>
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-subtle"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          id="global-search"
          type="search"
          autoComplete="off"
          placeholder={t(phone ? "header.searchPlaceholderShort" : "header.searchPlaceholder")}
          className="h-control w-full rounded-control border border-line bg-surface-2 pr-10 pl-9 text-sm text-ink placeholder:text-ink-subtle focus:border-primary focus:bg-surface focus:outline-none focus-visible:outline-none"
        />
        <kbd
          title={t("header.searchShortcut")}
          className="absolute top-1/2 right-2.5 hidden -translate-y-1/2 rounded border border-line-strong px-1.5 text-[0.7rem] leading-5 font-medium text-ink-subtle sm:block"
        >
          /
        </kbd>
      </form>

      <div className="ml-auto flex items-center gap-1">
        <ThemeMenu />
        <UserMenu />
      </div>
    </header>
  );
}
